import { differenceInCalendarDays } from 'date-fns';
import styled from 'styled-components';
import { Icon } from '../../../../../components/common/icon/Icon';
import { useCheckUserLoggedIn } from '../../../../../hooks/user/useCheckUserLoggedIn';
import { IProduct } from '../../../../../interfaces/Product.interfaces';
import { formatPrice } from '../../../../../utils/formatter/formatPrice.util';
import { getProductInstallmentInfo } from '../../../../../utils/getter/getProductInstallmentInfo.util';
import { PriceScreenSection } from '../../../components/PriceScreenSection';

interface ProductInfoAndPriceSectionProps {
  product: IProduct;
  category: 'PLUVA' | 'PARTNER';
}

export const ProductInfoAndPriceSection = ({ product, category }: ProductInfoAndPriceSectionProps) => {
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const { prepaymentAmount, biannualPaymentAmount, totalPaymentPeriod } = getProductInstallmentInfo(product.price);

  return (
    <Container>
      <MetaDataAndTitleContainer>
        <MetaDataContainer>
          <MetaData>{`${product.location}`}</MetaData>
          <MetaData>{'·'}</MetaData>
          <MetaData>{`${differenceInCalendarDays(new Date(), product.createdAt).toLocaleString()}일 전`}</MetaData>
        </MetaDataContainer>
        <Title>{product.title}</Title>
      </MetaDataAndTitleContainer>

      <PriceInfoContainer>
        {!isUserLoggedIn && <PriceScreenSection />}

        <PriceInfo>
          <PriceInfoLeftSection>
            <ProductPrice>{`${formatPrice(product.price)}원`}</ProductPrice>
          </PriceInfoLeftSection>
        </PriceInfo>

        <InsallmentSection>
          <InstallmentContainer>
            <InstallmentHeader>
              <Icon name={'Calendar24'} width={20} height={20} />
              <InstallmentTitleWrapper>
                <InstallmentHeaderTitle
                  color={'#FF3049'}
                >{`${totalPaymentPeriod}년간 할부시 연 납부액`}</InstallmentHeaderTitle>
                <InstallmentHeaderTitle color={'#232429'}>{'을 알려 드릴게요'}</InstallmentHeaderTitle>
              </InstallmentTitleWrapper>
            </InstallmentHeader>

            <InstallmentPriceInfoSection>
              <InstallmentPriceInfoWrapper>
                <InstallmentTitleDescriptionWrapper>
                  <InstllmentPriceTitle>{'선수금'}</InstllmentPriceTitle>
                  <InstallmentPriceDescription>{'(30% 기준)'}</InstallmentPriceDescription>
                </InstallmentTitleDescriptionWrapper>
                <InstallmentPrice>{`${formatPrice(
                  Math.floor(prepaymentAmount / 100000) * 100000,
                )}원`}</InstallmentPrice>
              </InstallmentPriceInfoWrapper>

              <HR />

              <InstallmentPriceInfoWrapper>
                <InstallmentTitleDescriptionWrapper>
                  <InstllmentPriceTitle>{'연 납부액'}</InstllmentPriceTitle>
                  <InstallmentPriceDescription>{'(연이율 약 4% 기준)'}</InstallmentPriceDescription>
                </InstallmentTitleDescriptionWrapper>
                <InstallmentPrice>{`약 ${formatPrice(
                  Math.floor(biannualPaymentAmount / 100000) * 100000,
                )}원`}</InstallmentPrice>
              </InstallmentPriceInfoWrapper>
            </InstallmentPriceInfoSection>
          </InstallmentContainer>

          <InstallmentDescriptionContainer>
            <InstallmentDescriptionWrapper>
              <Icon name={'ExclamationMarkCircle'} width={16} height={16} />
              <InstallmentDescription>{'선수금 비중은 상담을 통해 조정가능합니다. (최저 30%)'}</InstallmentDescription>
            </InstallmentDescriptionWrapper>
            <InstallmentDescriptionWrapper>
              <Icon name={'ExclamationMarkCircle'} width={16} height={16} />
              <InstallmentDescription>{'할부 금리는 개인 신용에 따라 변동됩니다. (최저 3%)'}</InstallmentDescription>
            </InstallmentDescriptionWrapper>
          </InstallmentDescriptionContainer>
        </InsallmentSection>
      </PriceInfoContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  padding: 32px var(--radius-large, 16px);
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-large, 16px);

  align-self: stretch;

  position: relative;
`;

const MetaDataAndTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`;

const MetaDataContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-small, 4px);
  align-self: stretch;
`;

const MetaData = styled.h2`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* System/Medium/m-light */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 85%; /* 11.9px */
`;
const Title = styled.h1`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const PriceInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;

  position: relative;
`;

const PriceInfo = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
`;

const ProductPrice = styled.span`
  color: var(--Light-Scheme-Color-primary-red, #ff3049);

  /* System/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 15.3px */
`;

const PriceInfoLeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-small, 4px);
  flex: 1 0 0;
`;

const InsallmentSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const InstallmentDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const InstallmentDescriptionWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  align-self: stretch;
`;

const InstallmentDescription = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Body/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 18px */
`;

const InstallmentContainer = styled.div`
  display: flex;
  padding-bottom: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const InstallmentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: stretch;

  padding: 16px;

  border-top-left-radius: var(--radius-medium, 8px);
  border-top-right-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey30, #e0e0eb);
`;

const InstallmentTitleWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const InstallmentHeaderTitle = styled.h2<{ color: string }>`
  color: ${(props) => props.color};

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 18.2px */
`;

const InstallmentPriceInfoSection = styled.div`
  display: flex;
  padding: 0px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const InstallmentPriceInfoWrapper = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
`;

const InstallmentTitleDescriptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const InstllmentPriceTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const InstallmentPriceDescription = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const InstallmentPrice = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 11.9px */
`;

const HR = styled.hr`
  width: 100%;
  height: 0px;

  border: 0.5px solid #e0e0eb;
`;
